// The boat's mode, chosen from a popover (TOM-526; Mike: the board name "opens
// a popup mode selector"). It replaces the mode sheet (TOM-193) everywhere:
// home's board name and its phone anchor button open it, and so does the
// shell header's mode button on every other boat page. A choice is sent to the
// boat through POST /v1/boats/:boatId/mode (ADR 0265), as the app does; the
// boat decides, and the header follows the mode it reports back.
//
// It opens under the button that opened it. Where that leaves no room (a short
// or narrow window), it is a small sheet at the foot instead. Arrow keys move
// between the choices, Escape or a click outside closes it, and the caller
// gives the focus back to the button.
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { api } from '../../api';
import { Icon } from '../ui/Icon';

interface Choice {
  key: string;
  label: string;
  detail: string;
  icon: string;
  body: unknown;
}

const CHOICES: readonly Choice[] = [
  {
    key: 'auto',
    label: 'Let the boat decide',
    detail: 'The boat works out its mode from its own readings.',
    icon: 'auto',
    body: { action: 'auto' },
  },
  {
    key: 'Anchored',
    label: 'Anchored',
    detail: 'At anchor or on a mooring.',
    icon: 'anchor',
    body: { action: 'pin', mode: 'Anchored' },
  },
  {
    key: 'Underway',
    label: 'Underway',
    detail: 'Under way, by engine or sail.',
    icon: 'underway',
    body: { action: 'pin', mode: 'Underway' },
  },
  {
    key: 'Docked',
    label: 'Docked',
    detail: 'Tied up at a dock or in a marina.',
    icon: 'docked',
    body: { action: 'pin', mode: 'Docked' },
  },
];

/** The popover's width, and the room it needs under its button. */
const WIDTH = 320;
const NEEDS_BELOW = 360;

/** Where the selector opens: under its button, or as a sheet when there is no room. */
export type ModeSelectorPlace =
  | { kind: 'popover'; left: number; top: number; width: number }
  | { kind: 'sheet' };

export function modeSelectorPlace(
  rect: Pick<DOMRect, 'left' | 'right' | 'bottom'> | null,
  viewport: { width: number; height: number },
  align: 'start' | 'end'
): ModeSelectorPlace {
  const width = Math.min(WIDTH, viewport.width - 32);
  if (rect === null || width < 260 || viewport.height - rect.bottom - 16 < NEEDS_BELOW) {
    return { kind: 'sheet' };
  }
  const wanted = align === 'end' ? rect.right - width : rect.left;
  const left = Math.max(16, Math.min(wanted, viewport.width - width - 16));
  return { kind: 'popover', left, top: rect.bottom + 8, width };
}

export function ModeSelector({
  boatId,
  anchor,
  align = 'start',
  currentMode,
  pinned,
  onClose,
  guest = false,
}: {
  boatId: string;
  /** The button that opened it, which it opens under. */
  anchor: HTMLElement | null;
  align?: 'start' | 'end';
  /** The mode the boat last reported, e.g. "Anchored"; null when unknown. */
  currentMode: string | null;
  /** The owner chose the mode, rather than the boat working it out. */
  pinned: boolean;
  onClose: () => void;
  /** A Try it guest (TOM-868): the boat sets its own mode, and no choice is offered. */
  guest?: boolean;
}) {
  const [sending, setSending] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [place] = useState<ModeSelectorPlace>(() =>
    modeSelectorPlace(
      anchor?.getBoundingClientRect() ?? null,
      { width: window.innerWidth, height: window.innerHeight },
      align
    )
  );
  const selected = pinned ? currentMode : 'auto';

  useEffect(() => {
    const rows = panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]');
    const checked = panelRef.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    (checked ?? rows?.[0])?.focus();
  }, []);

  // Escape closes it wherever the focus is: while a choice is sent its row is
  // disabled, and the focus leaves the panel. Taken in the capture phase, so a
  // chat or sheet under it doesn't close too.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    function onEscape(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current();
    }
    window.addEventListener('keydown', onEscape, true);
    return () => window.removeEventListener('keydown', onEscape, true);
  }, []);

  // Once a choice is sent, the focus goes back to its row, from where its
  // disabled row left it: the page, or the panel. Never from anywhere else: an
  // Escape as the boat answers has already given it to the button (TOM-852).
  const chosenRef = useRef<string | null>(null);
  useEffect(() => {
    if (sending !== null || chosenRef.current === null) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body && !panelRef.current?.contains(active)) {
      return;
    }
    panelRef.current
      ?.querySelector<HTMLElement>(`[data-testid="mode-choice-${chosenRef.current}"]`)
      ?.focus();
  }, [sending]);

  async function choose(choice: Choice) {
    chosenRef.current = choice.key;
    setSending(choice.key);
    setNote(null);
    try {
      await api(`/v1/boats/${boatId}/mode`, {
        method: 'POST',
        body: choice.body,
        idempotencyKey: crypto.randomUUID(),
      });
      setNote('Sent to the boat. The header changes when the boat confirms.');
    } catch {
      setNote('The boat could not be reached. Try again when it is online.');
    } finally {
      setSending(null);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    const rows = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []
    );
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? rows.length - 1
          : (at + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
    rows[next]?.focus();
  }

  const rows = CHOICES.map((c) => {
    const on = selected === c.key;
    return (
      <button
        key={c.key}
        type="button"
        role="menuitemradio"
        aria-checked={on}
        tabIndex={-1}
        className={`tb-menu__item mode-selector__row${on ? ' is-current' : ''}`}
        disabled={sending !== null}
        data-testid={`mode-choice-${c.key}`}
        onClick={() => void choose(c)}
      >
        <Icon name={c.icon} size={18} />
        <span className="mode-selector__text">
          <b>{c.label}</b>
          <span>{c.detail}</span>
        </span>
        {on && c.key !== 'auto' ? <span className="mode-selector__pinned">Pinned</span> : null}
        {on ? <Icon name="check" size={18} className="mode-selector__check" /> : null}
        {sending === c.key ? <span className="mode-selector__sending">Sending…</span> : null}
      </button>
    );
  });

  const body = (
    <>
      <p className="mode-selector__label" id="mode-selector-title">
        What is the boat doing?
      </p>
      {guest ? (
        <p className="tb-footnote mode-selector__note" data-testid="mode-selector-demo">
          {currentMode === null
            ? 'The boat is working out its mode.'
            : `The boat’s mode is ${currentMode}.`}{' '}
          Mode changes are not available in the demo.
        </p>
      ) : (
        rows
      )}
      {note !== null ? (
        <p className="tb-footnote mode-selector__note" role="status">
          {note}
        </p>
      ) : null}
    </>
  );

  if (place.kind === 'sheet') {
    return (
      <div className="shell-scrim" onClick={onClose}>
        <div
          ref={panelRef}
          className="shell-sheet mode-selector mode-selector--sheet"
          role="menu"
          aria-labelledby="mode-selector-title"
          data-testid="mode-selector"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
        >
          <span className="shell-sheet__grabber" aria-hidden="true" />
          {body}
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="mode-selector__away" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className="tb-menu mode-selector"
        role="menu"
        aria-labelledby="mode-selector-title"
        data-testid="mode-selector"
        style={{ left: place.left, top: place.top, width: place.width }}
        onKeyDown={onKeyDown}
      >
        {body}
      </div>
    </>
  );
}
